import type { WorkspaceAppId, WorkspaceBounds, WorkspaceWindow } from "@rakazo/contracts";
import {
  cascadeWorkspaceBounds,
  clampWorkspaceBounds,
  maximizedWorkspaceBounds,
  migrateWorkspaceWindows,
  nextWorkspaceWindowId,
  restoreWorkspaceWindows,
  snapWorkspaceBounds,
  workspaceDestination,
} from "@rakazo/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Location } from "react-router-dom";
import { clearEditorTabs } from "./workspace-local-state";

/** Array order is the stack: the last entry paints on top. */
export type OpenWindow = {
  id: string;
  app: WorkspaceAppId;
  name: string;
  /** What the window is showing, set by the app. Falls back to the app name. */
  title: string | null;
  location: Location;
  bounds: WorkspaceBounds;
  state: "normal" | "minimized" | "maximized";
  restore: WorkspaceBounds | null;
};

export const MAX_OPEN_WINDOWS = 12;

export const locationFor = (path: string, key: string): Location => {
  const url = new URL(path, "https://workspace.invalid");
  return { pathname: url.pathname, search: url.search, hash: "", state: null, key };
};
export const windowHref = (location: Location) => `${location.pathname}${location.search}`;
/** The exact resource a window is showing, normalised the same way saved paths are. */
const targetOf = (location: Location) =>
  workspaceDestination(windowHref(location))?.path ?? windowHref(location);
const windowId = (window: OpenWindow) => window.id;

/** Resolve a route once for both the first render and the committed window update. */
export function findWorkspaceWindow(
  windows: OpenWindow[],
  app: WorkspaceAppId,
  location: Location,
  focused: string | null,
  intent: "reuse" | "new" | "resource" = "reuse",
  preferredId?: string,
) {
  if (intent === "new") return undefined;
  return (
    windows.find((window) => window.id === preferredId && window.app === app) ??
    windows.find(
      (window) => window.app === app && targetOf(window.location) === targetOf(location),
    ) ??
    (intent === "reuse"
      ? (windows.find((window) => window.id === focused && window.app === app) ??
        windows.find((window) => window.id === app))
      : undefined)
  );
}

function readJson(key: string | null) {
  if (!key) return null;
  try {
    return JSON.parse(localStorage.getItem(key) ?? "null");
  } catch {
    return null;
  }
}

/**
 * Device-local window placement. The saved resources also stay in the v1 session key so rolling
 * back to the previous release still restores the same open apps, without their geometry.
 */
export function useWorkspaceWindows(scope: string | null, desktop: WorkspaceBounds) {
  const windowsKey = scope ? `andrew:workspace-windows:${scope}` : null;
  const sessionKey = scope ? `andrew:workspace:${scope}` : null;
  const desktopRef = useRef(desktop);
  desktopRef.current = desktop;

  const [windows, setWindows] = useState<OpenWindow[]>([]);
  /** Name of a window the limit turned away, surfaced once by the host. */
  const refused = useRef<string | null>(null);
  const [focus, setFocus] = useState<string | null>(null);
  const [desktopShown, setDesktopShown] = useState(false);
  const loaded = useRef(false);

  // Placement needs a measured desktop, so nothing opens a window before the first layout.
  // Opening one earlier would both mis-size it and be overwritten by the restore below.
  const ready = desktop.width > 0 && desktop.height > 0;
  if (!loaded.current && ready) {
    loaded.current = true;
    const saved =
      restoreWorkspaceWindows(readJson(windowsKey), desktop) ??
      migrateWorkspaceWindows(
        readJson(sessionKey),
        {
          split: readJson(scope ? `andrew:workspace-layout:${scope}` : null),
          ratio: readJson(scope ? `andrew:workspace-pane:${scope}` : null),
        },
        desktop,
      );
    const restored = saved.windows.flatMap<OpenWindow>((window) => {
      const destination = workspaceDestination(window.path);
      if (!destination || destination.id === "home") return [];
      return [
        {
          id: window.id,
          app: destination.id,
          name: destination.name,
          location: locationFor(window.path, window.id),
          title: window.title,
          bounds: window.bounds,
          state: window.state,
          restore: window.restore,
        },
      ];
    });
    if (restored.length) {
      setWindows(restored);
      setFocus(saved.focus ?? restored.at(-1)?.id ?? null);
    }
  }

  useEffect(() => {
    if (!windowsKey || !loaded.current) return;
    const record = {
      version: 2 as const,
      focus,
      windows: windows.map<WorkspaceWindow>((window) => ({
        id: window.id,
        path:
          workspaceDestination(windowHref(window.location))?.path ?? windowHref(window.location),
        title: window.title,
        bounds: window.bounds,
        state: window.state,
        restore: window.restore,
      })),
    };
    try {
      localStorage.setItem(windowsKey, JSON.stringify(record));
      if (sessionKey)
        localStorage.setItem(
          sessionKey,
          JSON.stringify({ version: 1, paths: record.windows.map((window) => window.path) }),
        );
    } catch {
      /* Open windows remain available in memory when storage is full. */
    }
  }, [windows, focus, windowsKey, sessionKey]);

  /**
   * Stored bounds are what the person arranged. A narrower viewport only changes where a window
   * is drawn, so shrinking the browser and widening it again brings the arrangement back instead
   * of flattening it permanently.
   */
  const placed = useMemo(
    () =>
      desktop.width > 0 && desktop.height > 0
        ? windows.map((window) => ({
            ...window,
            bounds:
              window.state === "maximized"
                ? maximizedWorkspaceBounds(desktop)
                : clampWorkspaceBounds(window.bounds, desktop),
          }))
        : windows,
    [windows, desktop],
  );

  const focusRef = useRef(focus);
  focusRef.current = focus;
  const openWindows = useRef(windows);
  openWindows.current = windows;

  const raise = useCallback((id: string) => {
    setFocus(id);
    setDesktopShown(false);
    setWindows((current) => {
      const index = current.findIndex((window) => window.id === id);
      if (index < 0 || index === current.length - 1) return current;
      const next = current.slice();
      const [window] = next.splice(index, 1);
      next.push(window!);
      return next;
    });
  }, []);

  /**
   * Opening a target that is already on screen focuses that window rather than adding a second
   * one; otherwise ordinary navigation reuses the focused window of that app, then its first window.
   * `intent: "new"` is an explicit request for another window of the same app, which is how two
   * notes or two conversations are kept side by side. `resource` only reuses an exact target,
   * and an explicit window ID restores the selected instance even when targets are identical.
   *
   * `focus` is false when a window is only tidying its own address, which must never pull it to
   * the foreground.
   */
  const show = useCallback(
    (
      destination: { id: WorkspaceAppId; name: string },
      location: Location,
      focus = true,
      intent: "reuse" | "new" | "resource" = "reuse",
      preferredId?: string,
    ) => {
      const current = openWindows.current;
      const existing = findWorkspaceWindow(
        current,
        destination.id,
        location,
        focusRef.current,
        intent,
        preferredId,
      );
      // Dropping a window here would bypass its close guard and could discard an unsaved draft
      // without asking. Refuse instead, and say so.
      if (!existing && current.length >= MAX_OPEN_WINDOWS) {
        refused.current = destination.name;
        return;
      }
      const id = existing?.id ?? nextWorkspaceWindowId(destination.id, current.map(windowId));
      // Older builds retained closed windows' tabs. A reused ID starts a new window;
      // restored and still-open windows keep their own strip, and drafts are untouched.
      if (!existing && destination.id === "editor") clearEditorTabs(scope, id);
      if (focus) {
        setDesktopShown(false);
        setFocus(id);
      }
      setWindows((windows) => {
        const desktopNow = desktopRef.current;
        const held = windows.find((window) => window.id === id);
        const updated: OpenWindow = held
          ? {
              ...held,
              location,
              state:
                held.state === "minimized" && focus
                  ? held.restore
                    ? "maximized"
                    : "normal"
                  : held.state,
            }
          : {
              id,
              app: destination.id,
              name: destination.name,
              title: null,
              location,
              bounds: cascadeWorkspaceBounds(windows.length, desktopNow),
              state: "normal",
              restore: null,
            };
        // Only a focused open restacks; tidying an address leaves the window where it was.
        if (held && !focus) return windows.map((window) => (window.id === id ? updated : window));
        return [...windows.filter((window) => window.id !== id), updated];
      });
    },
    [scope],
  );

  const close = useCallback((id: string) => {
    const event = new CustomEvent("workspace-close", { cancelable: true, detail: id });
    if (!window.dispatchEvent(event)) return false;
    window.dispatchEvent(new CustomEvent("workspace-closed", { detail: id }));
    setWindows((current) => current.filter((item) => item.id !== id));
    setFocus((current) =>
      current === id
        ? (openWindows.current.findLast(
            (window) => window.id !== id && window.state !== "minimized",
          )?.id ?? null)
        : current,
    );
    return true;
  }, []);

  const setState = useCallback(
    (id: string, state: OpenWindow["state"]) => {
      setWindows((current) =>
        current.map((window) => {
          if (window.id !== id) return window;
          // Maximizing keeps the arranged bounds untouched in `restore`, and the maximized
          // size is derived at display time, so neither step loses what was arranged.
          if (state === "maximized" && window.state !== "maximized")
            return { ...window, state, restore: window.bounds };
          if (state === "normal" && window.state === "maximized")
            return {
              ...window,
              state,
              bounds: window.restore ?? window.bounds,
              restore: null,
            };
          return { ...window, state };
        }),
      );
      if (state === "minimized")
        setFocus((current) =>
          current === id
            ? (openWindows.current.findLast(
                (window) => window.id !== id && window.state !== "minimized",
              )?.id ?? null)
            : current,
        );
      else raise(id);
    },
    [raise],
  );

  /** Bring a window forward, returning it from being put away to how it was last arranged. */
  const activate = useCallback(
    (id: string) => {
      setWindows((current) =>
        current.map((window) =>
          window.id === id && window.state === "minimized"
            ? { ...window, state: window.restore ? "maximized" : "normal" }
            : window,
        ),
      );
      raise(id);
    },
    [raise],
  );

  /** A view moves its own window to another resource without disturbing the others. */
  const navigateWindow = useCallback(
    (id: string, path: string) => {
      setWindows((current) =>
        current.map((window) =>
          window.id === id
            ? { ...window, location: locationFor(path, `${id}:${Date.now()}`) }
            : window,
        ),
      );
      raise(id);
    },
    [raise],
  );

  /** An app names its own window, so a put-away window is recognisable in the dock. */
  const setTitle = useCallback((id: string, title: string | null) => {
    setWindows((current) =>
      current.map((window) =>
        window.id === id && window.title !== title ? { ...window, title } : window,
      ),
    );
  }, []);

  const setBounds = useCallback((id: string, bounds: WorkspaceBounds) => {
    setWindows((current) =>
      current.map((window) =>
        window.id === id
          ? {
              ...window,
              bounds: clampWorkspaceBounds(bounds, desktopRef.current),
              // Dragging or resizing a maximized window returns it to a normal one.
              state: window.state === "maximized" ? "normal" : window.state,
              restore: window.state === "maximized" ? null : window.restore,
            }
          : window,
      ),
    );
  }, []);

  const snap = useCallback(
    (id: string, side: "left" | "right") => {
      setWindows((current) =>
        current.map((window) =>
          window.id === id
            ? {
                ...window,
                state: "normal",
                restore: null,
                bounds: snapWorkspaceBounds(side, desktopRef.current),
              }
            : window,
        ),
      );
      raise(id);
    },
    [raise],
  );

  /** Show Desktop hides the work without closing it; invoking it again brings it back. */
  const hidden = useRef<string[]>([]);
  const previousFocus = useRef<string | null>(null);
  const showDesktop = useCallback(() => {
    if (openWindows.current.every((window) => window.state === "minimized")) return;
    previousFocus.current = focusRef.current;
    hidden.current = openWindows.current
      .filter((window) => window.state !== "minimized")
      .map((window) => window.id);
    setWindows((current) =>
      current.map((window) =>
        window.state === "minimized" ? window : { ...window, state: "minimized" },
      ),
    );
    setDesktopShown(true);
    setFocus(null);
  }, []);
  const restoreDesktop = useCallback(() => {
    const restoring = new Set(hidden.current);
    setWindows((current) =>
      current.map((window) =>
        restoring.has(window.id)
          ? { ...window, state: window.restore ? "maximized" : "normal" }
          : window,
      ),
    );
    setDesktopShown(false);
    setFocus(previousFocus.current);
  }, []);
  const toggleDesktop = useCallback(() => {
    if (desktopShown) restoreDesktop();
    else showDesktop();
  }, [desktopShown, restoreDesktop, showDesktop]);

  const visible = useMemo(() => placed.filter((window) => window.state !== "minimized"), [placed]);

  const takeRefusal = useCallback(() => {
    const name = refused.current;
    refused.current = null;
    return name;
  }, []);

  return {
    ready,
    takeRefusal,
    // Callers render where a window currently fits; the arrangement itself is kept intact.
    windows: placed,
    visible,
    focus,
    desktopShown: desktopShown || visible.length === 0,
    show,
    raise,
    activate,
    close,
    setState,
    setTitle,
    navigateWindow,
    setBounds,
    snap,
    showDesktop,
    toggleDesktop,
  };
}
